import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Brain, Search, ChevronRight, Eye, User, Clock, Plus, Loader2, Diamond } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_STYLES = {
  'standard': { badge: 'bg-red-100 text-red-700' },
  'sop': { badge: 'bg-blue-100 text-blue-700' },
  'guide': { badge: 'bg-green-100 text-green-700' },
};

function DocCard({ doc, onClick }) {
  const style = TYPE_STYLES[doc.type] || TYPE_STYLES['guide'];
  return (
    <Card
      onClick={onClick}
      className="border border-slate-200 bg-white cursor-pointer rounded-xl hover:shadow-md hover:border-slate-300 transition-all"
      data-testid={`doc-${doc.id}`}
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-2">
          <span className={`text-[10px] px-2 py-0.5 rounded font-semibold uppercase ${style.badge}`}>{doc.type}</span>
          <ChevronRight className="w-4 h-4 text-slate-300" />
        </div>
        <h3 className="font-semibold text-[15px] text-slate-900 mb-1">{doc.title}</h3>
        <p className="text-sm text-slate-500 line-clamp-2">{doc.summary}</p>
        <div className="flex items-center gap-3 mt-3 text-[11px] text-slate-400">
          <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{doc.views || 0}</span>
          <span className="flex items-center gap-1"><User className="w-3 h-3" />{doc.created_by}</span>
        </div>
      </CardContent>
    </Card>
  );
}

export default function BusinessBrain() {
  const { user } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('single-diamond');
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [viewOpen, setViewOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [newDoc, setNewDoc] = useState({ title: '', type: 'guide', category: 'single-diamond', summary: '', content: '' });
  const isAdmin = ['owner', 'area_manager'].includes(user?.role);

  useEffect(() => { fetchDocuments(); }, []);

  const fetchDocuments = async () => {
    try { setLoading(true); const res = await axios.get(`${API_URL}/business-brain`); setDocuments(res.data); }
    catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  const openDoc = async (doc) => {
    setSelectedDoc(doc); setViewOpen(true);
    try { await axios.get(`${API_URL}/business-brain/${doc.id}`); } catch {}
  };

  const handleAddDoc = async () => {
    if (!newDoc.title.trim() || !newDoc.content.trim()) { toast.error('Title and content required'); return; }
    try {
      await axios.post(`${API_URL}/business-brain`, newDoc);
      toast.success('Document added');
      setAddOpen(false);
      setNewDoc({ title: '', type: 'guide', category: 'single-diamond', summary: '', content: '' });
      fetchDocuments();
    } catch { toast.error('Failed to add document'); }
  };

  const filtered = documents.filter(doc => {
    const matchesSearch = !searchQuery ||
      doc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doc.summary?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTab = doc.category === activeTab;
    return matchesSearch && matchesTab;
  });

  const singleCount = documents.filter(d => d.category === 'single-diamond').length;
  const doubleCount = documents.filter(d => d.category === 'double-diamond').length;

  return (
    <div className="space-y-6" data-testid="business-brain-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 bg-violet-100 rounded-xl flex items-center justify-center">
              <Brain className="w-5 h-5 text-violet-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Business Brain</h1>
              <p className="text-slate-500 text-sm">SOPs, procedures & operational knowledge</p>
            </div>
          </div>
        </div>
        {isAdmin && (
          <Button onClick={() => setAddOpen(true)} className="bg-violet-500 hover:bg-violet-600 text-white rounded-xl" data-testid="add-document-btn">
            <Plus className="w-4 h-4 mr-1" /> Add
          </Button>
        )}
      </div>

      {/* Diamond Tabs */}
      <div className="flex gap-3" data-testid="diamond-tabs">
        <button
          onClick={() => setActiveTab('single-diamond')}
          className={`flex-1 p-4 rounded-xl border-2 transition-all ${
            activeTab === 'single-diamond'
              ? 'border-amber-400 bg-amber-50'
              : 'border-slate-200 bg-white hover:border-slate-300'
          }`}
          data-testid="single-diamond-tab"
        >
          <div className="flex items-center gap-3">
            <Diamond className={`w-6 h-6 ${activeTab === 'single-diamond' ? 'text-amber-500' : 'text-slate-400'}`} />
            <div className="text-left">
              <p className={`font-bold text-sm ${activeTab === 'single-diamond' ? 'text-amber-700' : 'text-slate-700'}`}>
                Single Diamond
              </p>
              <p className="text-xs text-slate-500">{singleCount} docs &middot; Individual restaurant ops</p>
            </div>
          </div>
        </button>
        <button
          onClick={() => setActiveTab('double-diamond')}
          className={`flex-1 p-4 rounded-xl border-2 transition-all ${
            activeTab === 'double-diamond'
              ? 'border-violet-400 bg-violet-50'
              : 'border-slate-200 bg-white hover:border-slate-300'
          }`}
          data-testid="double-diamond-tab"
        >
          <div className="flex items-center gap-3">
            <div className="relative">
              <Diamond className={`w-6 h-6 ${activeTab === 'double-diamond' ? 'text-violet-500' : 'text-slate-400'}`} />
              <Diamond className={`w-4 h-4 absolute -top-1 -right-1 ${activeTab === 'double-diamond' ? 'text-violet-400' : 'text-slate-300'}`} />
            </div>
            <div className="text-left">
              <p className={`font-bold text-sm ${activeTab === 'double-diamond' ? 'text-violet-700' : 'text-slate-700'}`}>
                Double Diamond
              </p>
              <p className="text-xs text-slate-500">{doubleCount} docs &middot; Multi-unit management</p>
            </div>
          </div>
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Search documents..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          className="pl-10 h-11 rounded-xl border-slate-200"
        />
      </div>

      {/* Documents Grid */}
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-slate-300" /></div>
      ) : filtered.length === 0 ? (
        <Card className="border border-slate-200 rounded-xl">
          <CardContent className="py-14 text-center">
            <Brain className="w-12 h-12 mx-auto mb-3 text-slate-200" />
            <p className="text-slate-500 text-sm">No documents in this section</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(doc => <DocCard key={doc.id} doc={doc} onClick={() => openDoc(doc)} />)}
        </div>
      )}

      {/* View Document Dialog */}
      <Dialog open={viewOpen} onOpenChange={setViewOpen}>
        <DialogContent className="max-w-2xl rounded-2xl max-h-[85vh] overflow-y-auto">
          {selectedDoc && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-[10px] px-2 py-0.5 rounded font-semibold uppercase ${TYPE_STYLES[selectedDoc.type]?.badge || 'bg-slate-100 text-slate-600'}`}>
                    {selectedDoc.type}
                  </span>
                  <span className="text-[10px] px-2 py-0.5 rounded font-semibold uppercase bg-slate-100 text-slate-600">
                    {selectedDoc.category === 'single-diamond' ? 'Single Diamond' : 'Double Diamond'}
                  </span>
                </div>
                <DialogTitle className="text-lg">{selectedDoc.title}</DialogTitle>
                {selectedDoc.summary && <p className="text-sm text-slate-500 mt-1">{selectedDoc.summary}</p>}
              </DialogHeader>
              <div className="mt-4 bg-slate-50 rounded-xl p-5">
                <div className="prose prose-sm max-w-none">
                  {selectedDoc.content?.split('\n').map((line, i) => {
                    if (line.startsWith('## ')) return <h2 key={i} className="text-base font-bold mt-5 mb-2 text-slate-900">{line.replace('## ', '')}</h2>;
                    if (line.startsWith('### ')) return <h3 key={i} className="text-sm font-bold mt-4 mb-1.5 text-slate-800">{line.replace('### ', '')}</h3>;
                    if (line.startsWith('- ')) return <li key={i} className="ml-4 text-sm text-slate-600">{line.replace('- ', '')}</li>;
                    if (line.match(/^\d+\. /)) return <li key={i} className="ml-4 list-decimal text-sm text-slate-600">{line.replace(/^\d+\. /, '')}</li>;
                    if (line.trim() === '') return <div key={i} className="h-2" />;
                    return <p key={i} className="text-sm text-slate-600">{line}</p>;
                  })}
                </div>
              </div>
              <div className="flex items-center gap-4 mt-4 pt-3 border-t border-slate-100 text-[11px] text-slate-400">
                <span className="flex items-center gap-1"><User className="w-3 h-3" />{selectedDoc.created_by}</span>
                <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{selectedDoc.created_at ? format(new Date(selectedDoc.created_at), 'MMM d, yyyy') : ''}</span>
                <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{selectedDoc.views || 0} views</span>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Document Dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-lg rounded-2xl">
          <DialogHeader><DialogTitle>Add New Document</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <label className="text-sm font-medium text-slate-700">Title</label>
              <Input value={newDoc.title} onChange={e => setNewDoc({...newDoc, title: e.target.value})} placeholder="Document title" className="mt-1 rounded-xl" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-slate-700">Strategy</label>
                <Select value={newDoc.category} onValueChange={v => setNewDoc({...newDoc, category: v})}>
                  <SelectTrigger className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="single-diamond">Single Diamond</SelectItem>
                    <SelectItem value="double-diamond">Double Diamond</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Type</label>
                <Select value={newDoc.type} onValueChange={v => setNewDoc({...newDoc, type: v})}>
                  <SelectTrigger className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard">Standard</SelectItem>
                    <SelectItem value="sop">SOP</SelectItem>
                    <SelectItem value="guide">Guide</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Summary</label>
              <Input value={newDoc.summary} onChange={e => setNewDoc({...newDoc, summary: e.target.value})} placeholder="Brief description" className="mt-1 rounded-xl" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Content</label>
              <textarea value={newDoc.content} onChange={e => setNewDoc({...newDoc, content: e.target.value})} placeholder="Document content (## for headings, - for lists)" rows={8} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-violet-500" />
            </div>
            <Button onClick={handleAddDoc} className="w-full bg-violet-500 hover:bg-violet-600 text-white rounded-xl h-11">Add Document</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
